import type { ComponentChildren } from 'preact'
import styles from './StatusCard.module.css'

/**
 * A quiet, fixed-height summary that opens when asked (D-104 §1).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  FIXED HEIGHT WHEN CLOSED, AND THAT IS NOT A STYLE CHOICE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * C6.3: *"Today has to stay stable — a first block whose height depends on how
 * many areas the user chose cannot be learned by muscle memory."* A card that
 * grew with its content would move everything under it every time a value
 * arrived, which is the opposite of what a routine surface is for.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  NO SCORE, NO GRADE, NO STREAK, AND NO DIRECTION CARRIED BY COLOUR
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `trend` takes a **word**, not a sign or a hue. D-086 is explicit that semantic
 * colour never carries meaning alone, and the anti-streak rule is not limited to
 * the Today screen (D-062): a card is a place a percentage would look natural,
 * which is exactly why it must not become one.
 *
 * The arrow is decorative and duplicates the word beside it. Removing the arrow
 * loses nothing; removing the word loses the meaning.
 */
type Trend = 'up' | 'down' | 'level'

const ARROW: Readonly<Record<Trend, string>> = {
  up: '↑',
  down: '↓',
  level: '→',
}

interface StatusCardProps {
  /** What this is. Short — it sits above the value in a fixed-height row. */
  label: string
  /** The value, already formatted. `undefined` renders the empty state. */
  value?: string
  /** Shown when there is no value. A missing value is never a zero. */
  emptyLabel?: string
  /** Direction, as a word. Rendered beside an arrow that only repeats it. */
  trend?: { readonly direction: Trend; readonly word: string }
  /** Detail, behind the card's own disclosure. Absent means the card is inert. */
  children?: ComponentChildren
  /** The summary line for the detail. */
  detailLabel?: string
}

export function StatusCard({
  label,
  value,
  emptyLabel = 'Nothing yet',
  trend,
  children,
  detailLabel = 'Show detail',
}: StatusCardProps) {
  const head = (
    <>
      <span class={styles.label}>{label}</span>
      <span class={styles.value}>{value ?? emptyLabel}</span>
      {trend === undefined ? null : (
        <span class={styles.trend}>
          <span aria-hidden="true">{ARROW[trend.direction]}</span> {trend.word}
        </span>
      )}
    </>
  )

  if (children === undefined) {
    return <div class={styles.card}>{head}</div>
  }

  return (
    <details class={styles.card}>
      <summary class={styles.summary}>
        {head}
        <span class={styles.more}>{detailLabel}</span>
      </summary>
      <div class={styles.detail}>{children}</div>
    </details>
  )
}
